import { isEqual, uniqWith } from "lodash";

import { getResolvedWeekStart } from "../chart/chartTimeUnitUtils.js";
import { ChartTimeUnitConfig } from "../chart/types.js";
import {
  AggregateItem,
  AggregationTransform,
  Append,
  CalcExpr,
  Column,
  DataType,
  GroupByItem,
  HqlQuery,
  LiteralBool,
  LiteralNumber,
  LiteralString,
  LumpSpecification,
  OrderBy,
  SelectExpr,
  SelectItem,
  SelectionTransform,
  Transform,
  WhereExpr,
  WhereItem,
  WhereLump,
} from "../semantic-layer/generated/hex_sl_schema.js";
import { typedObjectKeys } from "../utils/typedObjects.js";

import { HqlTruncUnit } from "./types.js";

export function hqlCol(column: string): Column {
  return { name: column };
}

export function hqlBoolean(bool: boolean): LiteralBool {
  return { bool };
}

export function hqlStr(str: string): LiteralString {
  return { str };
}

export function hqlNumber(number: number): LiteralNumber {
  return { number };
}
/**
 * Stores parameter mappings for HQL queries. We use this to parameterize all
 * filter arguments to protect against SQL injection.
 *
 * - `literals`: Map of temporary parameter names to their actual argument values.
 *   hex-sl does not support jinja syntax with literal values, so we replace
 *   literal values with temporary variable names, then substitute them after
 *   generating the SQL query.
 *
 * - `jinjaRefs`: List of jinja variable references (e.g., `string_var` from
 *   `{{string_var}}`). These reference kernel variables and are handled
 *   differently: in HISC they become `ScalarFromVariable`, in non-HISC they
 *   remain as jinja syntax for kernel resolution.
 */
export type HqlNextTemporaryParamMapping = {
  literals: Record<string, string>;
  jinjaRefs: string[];
};

export function emptyParamMapping(): HqlNextTemporaryParamMapping {
  return { literals: {}, jinjaRefs: [] };
}

export function mergeParamMappings(
  a: HqlNextTemporaryParamMapping,
  b: HqlNextTemporaryParamMapping,
): HqlNextTemporaryParamMapping {
  return {
    literals: { ...a.literals, ...b.literals },
    jinjaRefs: [...a.jinjaRefs, ...b.jinjaRefs],
  };
}
export class HqlNextSpecBuilder {
  private baseDataset: string;

  private transforms: Transform[] = [];

  private firstDatasetIsBaseDataset: boolean = true;
  private parameters: Record<string, DataType> = {};
  private temporaryParamMapping: HqlNextTemporaryParamMapping =
    emptyParamMapping();

  constructor(baseDataset: string) {
    this.baseDataset = baseDataset;
  }

  public selection(
    selection: SelectionTransform,
    parameters?: Record<string, DataType>,
    temporaryParamMapping?: HqlNextTemporaryParamMapping,
  ): HqlNextSpecBuilder {
    if (this.transforms.length === 0 && selection.select.length > 0) {
      const dataset = selection.select[0]!.dataset;
      this.firstDatasetIsBaseDataset =
        dataset == null || dataset === this.baseDataset;
    }

    this.transforms.push(selection);
    this.parameters = { ...this.parameters, ...parameters };
    if (temporaryParamMapping != null) {
      this.temporaryParamMapping = mergeParamMappings(
        this.temporaryParamMapping,
        temporaryParamMapping,
      );
    }
    return this;
  }

  public aggregation(
    aggregation: AggregationTransform,
    parameters?: Record<string, DataType>,
    temporaryParamMapping?: HqlNextTemporaryParamMapping,
  ): HqlNextSpecBuilder {
    if (
      this.transforms.length === 0 &&
      ((aggregation.aggregate != null && aggregation.aggregate.length > 0) ||
        (aggregation.group_by != null && aggregation.group_by.length > 0))
    ) {
      const dataset = (aggregation.aggregate?.[0] ?? aggregation.group_by?.[0])!
        .dataset;
      this.firstDatasetIsBaseDataset =
        dataset == null || dataset === this.baseDataset;
    }

    this.transforms.push(aggregation);
    this.parameters = { ...this.parameters, ...parameters };
    if (temporaryParamMapping != null) {
      this.temporaryParamMapping = mergeParamMappings(
        this.temporaryParamMapping,
        temporaryParamMapping,
      );
    }
    return this;
  }

  public addSelections(append: Append): HqlSelectionBuilder {
    return new HqlSelectionBuilder(this, this.baseDataset, append);
  }

  public addAggregations(): HqlAggregationBuilder {
    return new HqlAggregationBuilder(this, this.baseDataset);
  }

  build(): {
    query: HqlQuery;
    temporaryParamMapping: HqlNextTemporaryParamMapping;
  } {
    // If the first dataset occurrence is the base dataset, then we should remove
    // referring to the dataset anywhere as the base dataset name changes in hex-sl
    // internally with each transform. But if the first one isn't the base dataset,
    // then we need to make sure to add the base dataset name to the dataset field
    // if the consumer omitted it.
    const resolveBaseDataset = (o: { dataset?: string | null }): void => {
      if (o.dataset == null && !this.firstDatasetIsBaseDataset) {
        o.dataset = this.baseDataset;
      } else if (
        o.dataset === this.baseDataset &&
        this.firstDatasetIsBaseDataset
      ) {
        o.dataset = undefined;
      }
    };

    this.transforms.forEach((transform) => {
      if ("select" in transform) {
        transform.select.forEach((select: SelectItem) => {
          resolveBaseDataset(select);
        });
        transform.where?.forEach((where) => {
          resolveBaseDataset(where);
        });
      } else {
        transform.aggregate?.forEach((aggregate) => {
          resolveBaseDataset(aggregate);
        });
        transform.group_by?.forEach((groupBy) => {
          resolveBaseDataset(groupBy);
        });
        transform.where?.forEach((where) => {
          resolveBaseDataset(where);
        });
      }
    });

    return {
      query: {
        base: this.baseDataset,
        transforms: this.transforms,
        // query datasets - for now we define everything at the top level dataset
        datasets: [],
        ...(typedObjectKeys(this.parameters).length > 0 && {
          parameters: this.parameters,
        }),
      },
      temporaryParamMapping: this.temporaryParamMapping,
    };
  }
}

abstract class HqlNextGenBuilder {
  public readonly builderType: "selection" | "aggregation" = "selection";
  protected parent: HqlNextSpecBuilder;
  protected baseDataset: string;
  protected params: Record<string, DataType> = {};
  protected temporaryParamMapping: HqlNextTemporaryParamMapping =
    emptyParamMapping();
  protected _filters: WhereItem[] = [];
  protected _orderBy: OrderBy | null = null;
  protected _limit: number | null = null;
  protected _offset: number | null = null;

  constructor(parent: HqlNextSpecBuilder, baseDataset: string) {
    this.parent = parent;
    this.baseDataset = baseDataset;
  }

  public filter(filter: WhereExpr | WhereLump): this {
    this._filters.push(filter);
    return this;
  }

  public orderBy(orderBy: OrderBy | null): this {
    this._orderBy = orderBy;
    return this;
  }

  public limit(limit: number | null): this {
    this._limit = limit;
    return this;
  }

  public offset(offset: number | null): this {
    this._offset = offset;
    return this;
  }

  public addParams(
    params: Record<string, DataType>,
    temporaryParamMapping: HqlNextTemporaryParamMapping = emptyParamMapping(),
  ): void {
    this.params = { ...this.params, ...params };
    this.temporaryParamMapping = mergeParamMappings(
      this.temporaryParamMapping,
      temporaryParamMapping,
    );
  }
}

export class HqlSelectionBuilder extends HqlNextGenBuilder {
  public override readonly builderType = "selection" as const;
  private append?: Append;
  private selects: SelectItem[] = [];

  constructor(
    parent: HqlNextSpecBuilder,
    baseDataset: string,
    append?: Append,
  ) {
    super(parent, baseDataset);
    this.append = append;
  }

  public select(select: SelectItem): HqlSelectionBuilder {
    this.selects.push(select);
    return this;
  }

  public buildSelection(): HqlNextSpecBuilder {
    this.parent.selection(
      {
        select: this.selects,
        append: this.append,
        where: this._filters,
        order_by: this._orderBy,
        limit: this._limit,
        offset: this._offset,
      },
      this.params,
      this.temporaryParamMapping,
    );

    return this.parent;
  }
}

export class HqlSelectExprBuilder {
  public readonly builderType = "selectExpr" as const;
  private expr: CalcExpr;

  constructor(private columnName: string) {
    this.expr = hqlCol(this.columnName);
  }

  public datecast(): HqlSelectExprBuilder {
    this.expr = {
      fun: "_chart_todatetime",
      args: [this.expr],
    };

    return this;
  }

  public numbercast(): HqlSelectExprBuilder {
    this.expr = {
      fun: "_chart_tonumber",
      args: [this.expr],
    };

    return this;
  }

  public datetrunc(
    unit: HqlTruncUnit,
    timeUnitConfig: ChartTimeUnitConfig | undefined,
  ): HqlSelectExprBuilder {
    const unitToFunTruncUnit = {
      [HqlTruncUnit.year]: "truncyear",
      [HqlTruncUnit.quarter]: "truncquarter",
      [HqlTruncUnit.month]: "truncmonth",
      [HqlTruncUnit.week]: "truncweek",
      [HqlTruncUnit.day]: "truncday",
      [HqlTruncUnit.hour]: "trunchour",
      [HqlTruncUnit.minute]: "truncminute",
      [HqlTruncUnit.second]: "truncsecond",
      [HqlTruncUnit.dayofweek]: "dayofweek",
    } as const;

    const fun =
      unit === HqlTruncUnit.week &&
      getResolvedWeekStart(timeUnitConfig) === "monday"
        ? "truncweekmonday"
        : unitToFunTruncUnit[unit];

    this.expr = {
      fun,
      args: [this.expr],
    };
    return this;
  }

  public build({
    as,
    dataset,
    lump,
  }: {
    as?: string;
    dataset?: string;
    lump?: LumpSpecification;
  }): SelectExpr {
    return {
      expr: this.expr,
      alias: as ?? this.columnName,
      dataset: dataset,
      ...(lump != null && { lump }),
    };
  }
}

export class HqlAggregationBuilder extends HqlNextGenBuilder {
  public override readonly builderType = "aggregation" as const;
  private aggregations: AggregateItem[] = [];
  private groupbys: GroupByItem[] = [];
  private details: SelectItem[] = [];
  private havings: string[] = [];

  public aggregation(aggregateItem: AggregateItem): HqlAggregationBuilder {
    this.aggregations.push(aggregateItem);

    return this;
  }

  public groupby(groupby: GroupByItem): HqlAggregationBuilder {
    this.groupbys.push(groupby);
    return this;
  }

  public detail(detail: SelectItem): HqlAggregationBuilder {
    this.details.push(detail);
    return this;
  }

  public having(havingClause: { expr: string }): HqlAggregationBuilder {
    this.havings.push(havingClause.expr);
    return this;
  }

  public getAggregation(index: number): AggregateItem | undefined {
    // Return a deep clone to prevent external mutation of the original aggregation
    return this.aggregations[index] != null
      ? structuredClone(this.aggregations[index])
      : undefined;
  }

  public buildAggregation(args?: {
    skipIfNoAggregations?: boolean;
  }): HqlNextSpecBuilder {
    const builder = this.parent;
    const groupBys = uniqWith(this.groupbys, isEqual);
    if (
      this.aggregations.length > 0 ||
      (groupBys.length > 0 && args?.skipIfNoAggregations !== true)
    ) {
      builder.aggregation(
        {
          group_by: groupBys,
          aggregate: this.aggregations,
          where: this._filters,
          order_by: this._orderBy,
          limit: this._limit,
          offset: this._offset,
          having: this.havings,
          detail: this.details,
        },
        this.params,
        this.temporaryParamMapping,
      );
    }
    return builder;
  }
}

export type HqlBuilder =
  | HqlSelectionBuilder
  | HqlAggregationBuilder
  | HqlSelectExprBuilder;

export type HqlGenBuilder = HqlSelectionBuilder | HqlAggregationBuilder;

export function isSelectionBuilder(
  builder: HqlBuilder,
): builder is HqlSelectionBuilder {
  return builder.builderType === "selection";
}

export function isAggregationBuilder(
  builder: HqlBuilder,
): builder is HqlAggregationBuilder {
  return builder.builderType === "aggregation";
}

export function isSelectExprBuilder(
  builder: HqlBuilder,
): builder is HqlSelectExprBuilder {
  return builder.builderType === "selectExpr";
}

export function isGenBuilder(builder: HqlBuilder): builder is HqlGenBuilder {
  return (
    builder.builderType === "selection" || builder.builderType === "aggregation"
  );
}
